import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import { pushCheckoutCardSelected, pushErrors } from 'common/gamp';
import integratedCheckout from 'communicator/external/checkout/integrated/index';
import queueEvents from 'communicator/internal/queue-events';
import {
  CommunicatorState,
  getState,
  saveCheckoutData,
  saveFlow,
  saveMeta,
  saveProfile,
  saveUser,
  UserProfile
} from 'communicator/store';
import {
  getAccessToken,
  getApiKey,
  getCardListPayload,
  getDcfExperience,
  getDpaTransactionOptions,
  getIsFeatureToggleEnabled,
  getIsNonVisaRecognizedUser,
  getIsVisaSRCi,
  getMeta,
  getSrcDpaId,
  getUser,
  getVIntentId
} from 'communicator/store/selectors';
import { isAddressValidated } from 'communicator/utils/address';
import { fetchCardList } from 'communicator/utils/api/card-list';
import { fetchRevokeIdTokens } from 'communicator/utils/api/collection';
import fetchEnrollCard from 'communicator/utils/api/enroll-card';
import { encryptData } from 'communicator/utils/encrypt';
import { isEnrollCardError } from 'communicator/utils/enroll-card-error';
import { getHeader } from 'communicator/utils/fetch';
import generateXAuthCodeFromAccessToken from 'communicator/utils/generate-x-auth-code';
import getGenericError from 'communicator/utils/generic-error';
import {
  getLockoutFlowLegacy,
  getOrchestratorLockoutFlow
} from 'communicator/utils/get-lockout-flow';
import convertIdTokenToAccessToken, { parseIdToken } from 'communicator/utils/id-token';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import {
  cvvLaunchHandler,
  dcfLaunchHandler,
  fidoLaunchHandler,
  launchIssuerOtpFlowHandler,
  srcOrchestrateBankAppHandler,
  threeDsLaunchHandler,
  vppLaunchHandler
} from 'communicator/utils/launch-handler-functions';
import parseExpiryFromAccessToken from 'communicator/utils/parse-expiry-from-token';
import { CardListProfile } from 'types/api/card-list';
import { Consumer } from 'types/emvco';
import {
  CheckoutCommunicatorPayload,
  CheckoutCommunicatorLegacyPayload,
  CheckoutRequest
} from 'types/visa-sdk/checkout';

export default async function checkout(
  event: HandlerEvent<CheckoutRequest>
): Promise<CheckoutCommunicatorLegacyPayload | CheckoutCommunicatorPayload | ValidationError> {
  try {
    const checkoutData = event.data.data;
    let accessToken = getAccessToken();
    const apikey = getApiKey();
    const isVisaSRCi = getIsVisaSRCi();
    const dcfExperience = getDcfExperience();
    const isIntegrated = dcfExperience === 'integrated';
    const isInit = getState().meta.init;
    const isCheckoutConfig = getState().config.checkout;

    saveCheckoutData(checkoutData);

    //START GENAI@GHCOPILOT
    const launchDcf = dcfLaunchHandler(event.data.launchHandlers.launchDcf);
    const isSplitSessionEnabled = getIsFeatureToggleEnabled('sdkSplitSession');
    const hasSrcDigitalCardId = Boolean(checkoutData.srcDigitalCardId);
    const isInitialized = Boolean(isInit);
    const hasNoAccessToken = !accessToken;
    const hasIdToken = Boolean(checkoutData.idToken);
    const isNotCheckoutConfig = !isCheckoutConfig;

    const shouldProceed =
      hasSrcDigitalCardId &&
      isInitialized &&
      hasNoAccessToken &&
      hasIdToken &&
      isSplitSessionEnabled &&
      isNotCheckoutConfig;

    //START GENAI@CHATGPT4
    if (shouldProceed) {
      //START GENAI@CHATGPT4
      const cardListPayload = getCardListPayload([checkoutData.idToken as string]);
      //END GENAI@GHCOPILOT

      cardListPayload.srcCorrelationId = checkoutData.srcCorrelationId;

      const cardListResponse = await fetchCardList({
        payload: cardListPayload
      });

      if (!cardListResponse.ok) {
        if (getOrchestratorLockoutFlow(cardListResponse)) {
          saveUser({
            accessToken: null,
            isLocked: true
          });

          // START GENAI@GHCOPILOT
          pushErrors('GET_SRC_PROFILE', cardListResponse.errorMessage);
          // END GENAI@GHCOPILOT

          markEnd('GET_SRC_PROFILE', { doFlush: true, failure: true, reason: 'AUTH_INVALID' });
          return error('AUTH_INVALID');
        }

        const err = getGenericError(cardListResponse);
        // START GENAI@GHCOPILOT
        pushErrors('GET_SRC_PROFILE', err);
        // END GENAI@GHCOPILOT
        markEnd('GET_SRC_PROFILE', { doFlush: true, failure: true, reason: err });
        return error(err);
      }

      const srcProfile: CardListProfile = cardListResponse.data.profiles[0];

      saveProfile({
        maskedCards: srcProfile.maskedCards
      });

      const newAccessToken = getHeader(cardListResponse, 'access_token');
      const { amrCookie, emailHash } = parseIdToken(srcProfile.authorization);
      const userUpdates: UserProfile = {};

      if (newAccessToken) {
        const expiresIn = parseExpiryFromAccessToken(newAccessToken) as number;
        userUpdates.accessToken = {
          expiresIn,
          isPartial: false,
          token: newAccessToken
        };
      }

      const refreshedIdToken = srcProfile.authorization;

      if (refreshedIdToken) {
        userUpdates.idToken = refreshedIdToken;
      }

      if (getIsNonVisaRecognizedUser(amrCookie)) {
        userUpdates.isNonVisaRecognizedUser = true;
      }

      if (emailHash) {
        userUpdates.emailHash = emailHash;
      }

      //START GENAI@GHCOPILOT
      //START GENAI@GHCOPILOT
      userUpdates.email = srcProfile.maskedConsumer.maskedEmailAddress;

      //END GENAI@GHCOPILOT

      if (srcProfile.maskedConsumer.maskedConsumerIdentity.identityType !== 'EMAIL_ADDRESS') {
        userUpdates.phone = srcProfile.maskedConsumer.maskedConsumerIdentity.maskedIdentityValue;
      }
      //END GENAI@GHCOPILOT

      saveUser(userUpdates);

      //START GENAI@GHCOPILOT
      const metaUpdates: Partial<CommunicatorState['meta']> = {
        split: true
      };

      saveMeta(metaUpdates);
      //END GENAI@GHCOPILOT
      //END GENAI@CHATGPT4
    }
    //END GENAI@CHATGPT4

    // TODO: should we branch for integrated before or after converting idToken?
    if (isIntegrated) {
      return integratedCheckout(
        event.data,
        srcOrchestrateBankAppHandler(event.data.launchHandlers.launchSRCBankApp),
        launchIssuerOtpFlowHandler(event.data.launchHandlers.launchSRCIssuerOtp),
        cvvLaunchHandler(event.data.launchHandlers.launchCvv),
        launchDcf,
        fidoLaunchHandler(event.data.launchHandlers.launchFIDO),
        threeDsLaunchHandler(event.data.launchHandlers.launchThreeDs),
        threeDsLaunchHandler(event.data.launchHandlers.launchThreeDsWithOnLoad),
        vppLaunchHandler(event.data.launchHandlers.launchVPP),
        event.data.launchHandlers.launchOtpValidation
      );
    }

    markStart('CHECKOUT_LEGACY');
    markStart('CHECKOUT_LEGACY_ENROLL_CARD');

    // If Visa SRCi is not already open, launch DCF immediately to avoid popup
    // blocker and show an immediate loader screen. When Visa SRCi is already
    // open, we want to complete all the api calls prior to routing the user to
    // DCF in case of errors.
    if (!isVisaSRCi) {
      launchDcf();
    }

    // If we don't already have an accessToken (from /recognized or /probe), and
    // there is an idToken in the request, convert it to an accessToken
    if (checkoutData.idToken) {
      if (accessToken?.token && !accessToken.isPartial) {
        // Revoke idTokens if accessToken is available, fire and forget
        fetchRevokeIdTokens({
          accessToken: accessToken.token,
          idTokens: [checkoutData.idToken],
          mark: 'checkout_legacy_revoke_id_tokens'
        });
      } else {
        const tokenResponse = await convertIdTokenToAccessToken({
          idToken: checkoutData.idToken,
          marks: { token: 'checkout_integrated_auth_code_from_access_token' }
        });

        if (!tokenResponse.ok && getLockoutFlowLegacy(tokenResponse)) {
          saveUser({
            accessToken: null,
            isLocked: true
          });

          // START GENAI@GHCOPILOT
          pushErrors('Token conversion', tokenResponse.errorMessage);
          // END GENAI@GHCOPILOT

          return error('AUTH_INVALID');
        }
      }
    }

    // accessToken is not required at this point for enroll card flow. However, if
    // we have one, we need to convert it to an xAuthCode which the DCF will use
    // to generate its own access token
    accessToken = getAccessToken();
    if (accessToken?.token && !accessToken.isPartial) {
      await generateXAuthCodeFromAccessToken({
        accessToken: accessToken.token,
        mark: 'checkout_legacy_auth_code_from_access_token'
      });
    } else if (accessToken?.isPartial) {
      // card first flow?
    }

    // Checkout flow
    if (checkoutData.srcDigitalCardId || !checkoutData.encryptedCard) {
      const cardIdMapping = getMeta().cardIdMapping;
      const selectedCardId =
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        cardIdMapping[checkoutData.srcDigitalCardId!] ?? checkoutData.srcDigitalCardId;

      pushCheckoutCardSelected();

      saveFlow({
        toRoute: 'review'
      });

      saveMeta({
        // appLoaded event waits until this flag is set to return configs to the app
        isCheckoutDataReady: true,
        selectedCardId
      });

      if (isVisaSRCi) {
        launchDcf();
      }

      markEnd('CHECKOUT_LEGACY', { doFlush: true });
      return {};
    }

    if (checkoutData.shippingAddress && !isAddressValidated(checkoutData.shippingAddress)) {
      queueEvents({
        source: window,
        origin: window.location.origin,
        data: {
          E: 'BATCHED_PERFORMANCE_LOG',
          ERR: undefined,
          ETIME: 1687459723,
          mt: undefined,
          options: {
            reason:
              'Characters passed in Shipping Address exceeded maximum character limit so shippingAddress is removed from the checkout request payload'
          },
          STIME: 1687459714,
          TTAKEN: 1,
          TY: 'PERFORMANCE',
          U: ''
        }
      });
    }

    if (checkoutData.consumer?.mobileNumber && !checkoutData.consumer.mobileNumber.phoneNumber) {
      // START GENAI@GHCOPILOT
      pushErrors('CHECKOUT_LEGACY', 'consumer.mobileNumber.phoneNumber is required');
      // END GENAI@GHCOPILOT
      return error('INVALID_PARAMETER', 'consumer.mobileNumber.phoneNumber is required');
    }

    // Enroll Card flow
    markStart('checkout_legacy_encrypt_consumer_data');
    const encryptedConsumer = await encryptData<Consumer>(checkoutData.consumer);
    markEnd('checkout_legacy_encrypt_consumer_data');
    const dpaTransactionOptions = getDpaTransactionOptions();
    const user = getUser();

    markStart('checkout_legacy_enroll_card');
    const enrollCardResponse = await fetchEnrollCard({
      accessToken: accessToken?.isPartial ? undefined : accessToken?.token,
      apikey,
      dpaTransactionOptions,
      encryptedBillingAddress: checkoutData.encryptedBillingAddress,
      encryptedCard: checkoutData.encryptedCard,
      encryptedConsumer,
      intentId: getVIntentId(),
      // Only pass srcCorrelationId if a user is recognized or logged in
      srcCorrelationId:
        user?.isLoggedInOtpUser || user?.isRecognizedUser ? getVIntentId() : undefined,
      srcDpaId: getSrcDpaId()
    });

    markEnd('checkout_legacy_enroll_card');

    if (!enrollCardResponse.ok) {
      markEnd('checkout_legacy_enroll_card', { failure: true });
      // START GENAI@GHCOPILOT
      pushErrors('CHECKOUT_LEGACY_ENROLL_CARD', enrollCardResponse.errorMessage);
      // END GENAI@GHCOPILOT
      return error(
        enrollCardResponse.errorObject.reason ||
          String(enrollCardResponse.errorCode) ||
          'CARD_ADD_FAILED'
      );
    }

    if (isEnrollCardError(enrollCardResponse.data, dpaTransactionOptions)) {
      markEnd('checkout_legacy_enroll_card', { failure: true });
      // START GENAI@GHCOPILOT
      pushErrors('CHECKOUT_LEGACY_ENROLL_CARD', 'PENDING_AVS or UNSUPPORTED COUNTRY');
      // END GENAI@GHCOPILOT
      return error('CARD_ADD_FAILED');
    }

    markEnd('checkout_legacy_enroll_card');

    const isLocked = enrollCardResponse.data.maskedConsumer?.status === 'LOCKED';

    /**
     * For logged in otp user who did NOT pass consumer.consumerIdentity in
     * checkout request, use unmasked email from maskedConsumer object for
     * VCO-14074. Per F20000, maskConsumer may not be sent back if no consumer
     * in request, add default value
     */
    const email =
      !isLocked && checkoutData.consumer?.consumerIdentity
        ? checkoutData.consumer.consumerIdentity.identityValue
        : (enrollCardResponse.data.maskedConsumer?.maskedConsumerIdentity.maskedIdentityValue ??
          '');

    const userProfileUpdates: Partial<CommunicatorState['user']> = {
      email,
      isLocked,
      mobileNumber: checkoutData.consumer?.mobileNumber
    };

    // if xAuthCode returned in enrollCard response, save to token state to pass to DCF
    const enrollCardXAuthCode = getHeader(enrollCardResponse, 'x-auth-code');

    if (enrollCardXAuthCode) {
      userProfileUpdates.xAuthCode = enrollCardXAuthCode;
    }

    saveUser(userProfileUpdates);

    if (enrollCardResponse.data.srcCorrelationId) {
      saveMeta({
        vIntentId: enrollCardResponse.data.srcCorrelationId
      });
    }

    saveFlow({
      addCardInfo: {
        cardSuffix: enrollCardResponse.data.maskedCard.panLastFour,
        enrollCardResponse: enrollCardResponse.data,
        networkId: 'VISA',
        panJWE: enrollCardResponse.data.customOutputData.encryptedCard || checkoutData.encryptedCard
      },
      toRoute: 'enroll-card'
    });

    saveMeta({
      // appLoaded event waits until this flag is set to return configs to the app
      isCheckoutDataReady: true
    });

    // For Visa SRCi, launch DCF once all api calls are completed. This avoids
    // jank when transitioning from SRCi->DCF by preventing a transition when
    // an error occurs
    if (isVisaSRCi) {
      launchDcf();
    }

    markEnd('CHECKOUT_LEGACY_ENROLL_CARD', { doFlush: true });
    return {};
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    pushErrors('CHECKOUT_LEGACY', err);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    markEnd('CHECKOUT_LEGACY', { doFlush: true, failure: true, reason: sErr });
    return error('CHECKOUT_LEGACY_FAILED', sErr);
    // END GENAI@GHCOPILOT
  }
}
